import { useMemo, useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Container, DataGrid, KeenIcon } from '@/components';
import { useGetUserKYCQuery } from '@/services/KYCService';
import { UserKYCData } from '@/types/user-kyc.types';
import { KYCStatus, KYCType } from '@/constant';
import { displayKycCountry } from '@/utils/countryName';
import { useNavigate } from 'react-router-dom';

type PaginationState = {
  pageIndex: number;
  pageSize: number;
};

const UserKYCTable = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const {
    data: kycDataResponse,
    isLoading,
    isFetching,
    isError,
  } = useGetUserKYCQuery(
    {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedSearchTerm.trim() || undefined,
    },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    }
  );

  // Handle both array response and paginated response
  const kycData = useMemo(() => {
    if (!kycDataResponse) return [];
    // Check if it's a paginated response
    if ('data' in kycDataResponse && Array.isArray(kycDataResponse.data)) {
      return kycDataResponse.data;
    }
    // Otherwise it's a direct array
    if (Array.isArray(kycDataResponse)) {
      return kycDataResponse;
    }
    return [];
  }, [kycDataResponse]);

  // Extract pagination info if available
  const totalItems =
    kycDataResponse && 'totalItems' in kycDataResponse
      ? kycDataResponse.totalItems
      : kycData.length;
  const totalPages =
    kycDataResponse && 'totalPages' in kycDataResponse
      ? kycDataResponse.totalPages
      : Math.ceil((totalItems || 0) / pagination.pageSize);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case KYCStatus.PENDING:
        return <span className="badge badge-info">Pending</span>;
      case KYCStatus.UNDER_REVIEW:
        return <span className="badge badge-warning">Under Review</span>;
      case KYCStatus.APPROVED:
        return <span className="badge badge-success">Approved</span>;
      case KYCStatus.REJECTED:
        return <span className="badge badge-danger">Rejected</span>;
      case KYCStatus.REQUIRES_UPDATE:
        return <span className="badge badge-warning">Requires Update</span>;
      case KYCStatus.NOT_REQUIRED:
        return <span className="badge badge-secondary">Not Required</span>;
      case KYCStatus.ON_HOLD:
        return <span className="badge badge-warning">On Hold</span>;
      case KYCStatus.RESUBMITTED:
        return <span className="badge badge-info">Resubmitted</span>;
      case KYCStatus.DRAFT:
        return <span className="badge badge-secondary">Not Submitted</span>;
      default:
        return <span className="badge badge-secondary">Unknown</span>;
    }
  };

  const getEntityTypeLabel = (entityType: string) => {
    switch (entityType) {
      case KYCType.HARBOR:
        return 'Harbor';
      case KYCType.CLUB:
        return 'Club';
      case KYCType.SERVICE_PROVIDER:
        return 'Service Provider';
      default:
        return entityType;
    }
  };

  const columns = useMemo<ColumnDef<UserKYCData>[]>(
    () => [
      {
        accessorKey: 'entity_type',
        header: 'Entity Type',
        cell: ({ row }) => (
          <span className="block">
            {getEntityTypeLabel(row.original.entity_type)}
          </span>
        ),
      },
      {
        accessorKey: 'entity.name',
        header: 'Entity Name',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              minWidth: 0,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.entity.name}
          >
            {row.original.entity.name}
          </span>
        ),
      },
      {
        accessorKey: 'legal_name',
        header: 'Legal Name',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              minWidth: 0,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.legal_name}
          >
            {row.original.legal_name}
          </span>
        ),
      },
      {
        accessorKey: 'business_id',
        header: 'Business ID',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              minWidth: 0,
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.business_id}
          >
            {row.original.business_id}
          </span>
        ),
      },
      {
        accessorKey: 'vat_id',
        header: 'VAT ID',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              minWidth: 0,
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.vat_id ?? undefined}
          >
            {row.original.vat_id}
          </span>
        ),
      },
      {
        accessorKey: 'city',
        header: 'City',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              minWidth: 0,
              maxWidth: '120px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.city}
          >
            {row.original.city}
          </span>
        ),
      },
      {
        accessorKey: 'country',
        header: 'Country',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              minWidth: 0,
              maxWidth: '120px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={displayKycCountry(row.original.country)}
          >
            {displayKycCountry(row.original.country)}
          </span>
        ),
      },
      {
        accessorKey: 'kyc_status',
        header: 'Status',
        cell: ({ row }) => getStatusBadge(row.original.kyc_status),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          const status = row.original.kyc_status;
          const canEdit =
            status !== KYCStatus.APPROVED && status !== KYCStatus.NOT_REQUIRED;

          return (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  navigate(`/user-kyc/view/${row.original.id}`, {
                    state: { kycData: row.original },
                  })
                }
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="View"
                aria-label={`View KYC for ${row.original.entity.name}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
              {canEdit && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/user-kyc/edit/${row.original.id}`, {
                      state: { kycData: row.original },
                    })
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="Edit"
                  aria-label={`Edit KYC for ${row.original.entity.name}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="pencil"
                    className="text-gray-500"
                  />
                </button>
              )}
            </div>
          );
        },
        meta: { className: 'min-w-[100px]' },
      },
    ],
    [navigate]
  );

  return (
    <Container>
      <div
        className="card card-grid"
        style={{ minWidth: 0, width: '100%', overflowX: 'auto' }}
      >
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search KYC..."
                className="border dark:bg-gray-200 rounded-md py-2 px-3 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 contrast:bg-transparent"
                value={searchTerm}
                onChange={handleSearch}
                aria-label="Search KYC"
              />
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="w-4 h-4 absolute left-3 top-2.5 text-gray-400"
              />
            </div>
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-4">Loading KYC records...</div>
          ) : isError ? (
            <div className="text-center py-4 text-red-500">
              Failed to load KYC records. Please try again.
            </div>
          ) : !kycData || kycData.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No KYC records found.
            </div>
          ) : (
            <DataGrid
              columns={columns}
              data={kycData}
              rowSelect={false}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                totalItems: totalItems,
                totalPages: totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'legal_name', desc: false }]}
              serverSide={
                kycDataResponse && 'data' in kycDataResponse ? true : false
              }
            />
          )}
        </div>
      </div>
    </Container>
  );
};

export default UserKYCTable;
